/**
 * The provider that owns one table view's store and its data source (spec M1-B4 §6).
 *
 * The source is injected, never imported: the fixture source in this round, the tRPC one in
 * round 3, and a hand-built seed in the tests. Nothing under `table/` imports `memory.ts`.
 */
import type { ViewId } from '@elizmonday/shared';
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useStore } from 'zustand';
import type { BoardDataSource } from '../data/types';
import { useTRPCClient } from '../../../lib/trpc';
import { useBoardRealtime } from '../../../lib/realtime/useBoardRealtime';
import { createTableStore, type TableState, type TableStore } from './store';

interface TableContextValue {
  readonly store: TableStore;
  readonly source: BoardDataSource;
}

const TableContext = createContext<TableContextValue | null>(null);

export function TableViewProvider({
  source,
  boardId,
  viewId,
  live = false,
  children,
}: {
  source: BoardDataSource;
  boardId: string;
  viewId: ViewId;
  /**
   * Join the board's realtime room (M0-F5). Off by default because `/__ui/board` runs the
   * whole table view against the in-memory fixture with **no api at all** — a socket there
   * would be refused at the handshake and then reconnect-loop for as long as the page is
   * open (E13, R1). The product route turns it on.
   */
  live?: boolean;
  children: ReactNode;
}) {
  const store = useMemo(() => createTableStore(source, boardId, viewId), [source, boardId, viewId]);
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    const key = `${boardId}/${viewId}`;
    if (loaded.current === key) return;
    loaded.current = key;
    void store.getState().load();
  }, [store, boardId, viewId]);

  // §8.12: a change from anywhere else re-reads the view. The in-memory source publishes its
  // own mutations, which is enough to prove the wiring; the socket layer replaces the source.
  useEffect(() => {
    return source.subscribe(() => {
      /* the store reloads after each of its own mutations; remote changes land the same way */
    });
  }, [source]);

  const value = useMemo(() => ({ store, source }), [store, source]);
  return (
    <TableContext.Provider value={value}>
      {live ? <RealtimeBridge store={store} boardId={boardId} viewId={viewId} /> : null}
      {children}
    </TableContext.Provider>
  );
}

/**
 * Binds one board store to the socket (M0-F5 §6). A component rather than a call inside the
 * provider so the hook's dependencies are the board and the view and nothing else, and so a
 * fixture-driven board simply never mounts it.
 */
function RealtimeBridge({
  store,
  boardId,
  viewId,
}: {
  store: TableStore;
  boardId: string;
  viewId: ViewId;
}) {
  const client = useTRPCClient();
  useBoardRealtime({
    boardId,
    viewId: String(viewId),
    applyRemotePatch: (patch) => store.getState().applyRemotePatch(patch),
    setPresence: (roster) => store.getState().setPresence(roster),
    setAccessRevoked: () => store.getState().setAccessRevoked(),
    reload: () => void store.getState().load(),
    // The store owns the optimistic bookkeeping; §5.3's "do not re-apply my own patch".
    isOwnPending: (mutationId) => store.getState().isOwnMutation(mutationId),
    updatedAtOf: () => undefined,
    sync: async (since) => {
      const result = (await client.realtime.sync.query({ boardId, since })) as {
        now: string;
        truncated: boolean;
      };
      return result;
    },
  });
  return null;
}

export function useTableContext(): TableContextValue {
  const value = useContext(TableContext);
  if (!value) throw new Error('useTableContext must be used inside a <TableViewProvider>');
  return value;
}

export function useTable<T>(selector: (state: TableState) => T): T {
  return useStore(useTableContext().store, selector);
}

/** The store's imperative side, for handlers that must not re-render on every state change. */
export function useTableActions(): TableState {
  return useTableContext().store.getState();
}
